import { useEffect, useMemo, useState } from "react";
import Footer from "../components/Footer";
import "../styles/Inscription.css";

const PROGRAM_OPTIONS = [
  { value: "cqp", label: "CQP" },
  { value: "formation", label: "Formation" },
  { value: "masterclass", label: "Masterclass" },
];

function getStripePaymentLink(program) {
  const linksByProgram = {
    cqp: process.env.REACT_APP_STRIPE_PAYMENT_LINK_CQP,
    formation: process.env.REACT_APP_STRIPE_PAYMENT_LINK_FORMATION,
    masterclass: process.env.REACT_APP_STRIPE_PAYMENT_LINK_MASTERCLASS,
  };

  const link = linksByProgram[program] || process.env.REACT_APP_STRIPE_PAYMENT_LINK || "";
  return typeof link === "string" ? link.trim() : "";
}

function trackCheckoutRouting(method, program) {
  const payload = {
    event: "checkout_routing",
    method,
    program,
    timestamp: new Date().toISOString(),
  };

  try {
    window.sessionStorage.setItem("lehyve_last_checkout_routing", JSON.stringify(payload));
  } catch (_error) {
    // Ignore storage errors (private mode, blocked storage, etc.)
  }

  if (window.dataLayer && Array.isArray(window.dataLayer)) {
    window.dataLayer.push(payload);
  }

  if (typeof window.gtag === "function") {
    window.gtag("event", "checkout_routing", {
      method,
      program,
    });
  }

  if (process.env.NODE_ENV !== "production") {
    // Dev-only visibility to troubleshoot checkout routing.
    console.info("[checkout_routing]", payload);
  }
}

export default function Inscription() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    program: "cqp",
    paymentNow: true,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedProgramLabel = useMemo(() => {
    const option = PROGRAM_OPTIONS.find((item) => item.value === form.program);
    return option ? option.label : "Programme";
  }, [form.program]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const sendContactEmail = () => {
    const subject = encodeURIComponent(`Inscription ${selectedProgramLabel}`);
    const body = encodeURIComponent(
      `Bonjour,\n\nJe souhaite m'inscrire au programme ${selectedProgramLabel}.\n\nNom : ${form.lastName}\nPrénom : ${form.firstName}\nEmail : ${form.email}\n\nMerci.`
    );
    window.location.href = `mailto:contact@le-hyve.com?subject=${subject}&body=${body}`;
  };

  const redirectToPaymentLink = () => {
    const paymentLink = getStripePaymentLink(form.program);
    if (!paymentLink) {
      return false;
    }
    trackCheckoutRouting("payment_link_direct", form.program);
    window.location.href = paymentLink;
    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage("");
    setInfoMessage("");
    setIsSubmitting(true);

    if (form.paymentNow) {
      if (!redirectToPaymentLink()) {
        setErrorMessage(
          "Lien de paiement indisponible pour ce programme. Merci de réessayer dans quelques instants."
        );
      }
      setIsSubmitting(false);
      return;
    }

    setInfoMessage("Redirection vers votre messagerie...");
    sendContactEmail();
    setIsSubmitting(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentState = params.get("payment");
    if (paymentState === "success") {
      setInfoMessage("Paiement confirmé. Merci pour votre inscription.");
    } else if (paymentState === "cancel") {
      setErrorMessage("Paiement annulé. Vous pouvez réessayer.");
    }
  }, []);

  return (
    <>
      <section className="inscription-hero">
        <h1>Inscription</h1>
        <p>Choisissez votre programme et finalisez votre inscription en quelques clics.</p>
      </section>

      <section className="inscription-wrapper">
        <div className="inscription-grid">
          <form className="inscription-form" onSubmit={handleSubmit}>
            <h2>Vos informations</h2>

            <div className="inscription-row">
              <div>
                <label htmlFor="firstName">Prénom</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="Votre prénom"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName">Nom</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Votre nom"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="email@example.com"
              value={form.email}
              onChange={handleChange}
              required
            />

            <label htmlFor="program">Programme</label>
            <select id="program" name="program" value={form.program} onChange={handleChange}>
              {PROGRAM_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <label className="inscription-checkbox" htmlFor="paymentNow">
              <input
                id="paymentNow"
                type="checkbox"
                name="paymentNow"
                checked={form.paymentNow}
                onChange={handleChange}
              />
              <span>Payer maintenant avec Stripe</span>
            </label>

            <button type="submit" className="inscription-submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Redirection..."
                : form.paymentNow
                ? "Continuer vers le paiement"
                : "Envoyer la demande"}
            </button>

            {errorMessage ? <p className="inscription-error">{errorMessage}</p> : null}
            {infoMessage ? <p className="inscription-info">{infoMessage}</p> : null}
          </form>

          <aside className="inscription-summary">
            <h3>Recapitulatif</h3>
            <p>
              Programme sélectionné : <strong>{selectedProgramLabel}</strong>
            </p>
            <p className="inscription-note">
              Votre paiement est traité sur une page Stripe sécurisée. Aucune information bancaire
              n'est stockée sur notre site.
            </p>
          </aside>
        </div>
      </section>

      <Footer backgroundColor="black" />
    </>
  );
}
